import * as React from "react";
import { useState } from "react";
import { Box, Typography, Popover } from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { CARD_BG, AMBER, BG } from "../../Global/colors";
import { FONT_FAMILY, BORDER_STRONG, parseLocalDate } from "./designTokens";
import { RANGE_PRESETS } from "./HS/dateRangePresets";
import { FAGhostButton, FAPrimaryButton } from "./FAControls";

// Port of the suite-shared `.neura-date-control.date-range-pill` + `.neura-date-popover`
// (NeuraDesigns/assets/neura.css + neura.js), whose own header comment calls it
// "one control across the suite... maps to <DateRangePicker> in React/MUI".
// Only team.html and incident.html use it — hs-history.html / hs-export.html
// deliberately hand-roll their own From/To + chip row instead, which is why
// History.js and Export.js keep that different (and equally correct) treatment.

function formatRange(from, to) {
  if (!from || !to) return "—";
  const start = parseLocalDate(from);
  const end = parseLocalDate(to);
  const sameYear = start.getFullYear() === end.getFullYear();
  const startLabel = start.toLocaleDateString(undefined, { day: "numeric", month: "short", ...(sameYear ? {} : { year: "numeric" }) });
  const endLabel = end.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  return `${startLabel} – ${endLabel}`;
}

const FIELD_INPUT_SX = {
  width: "100%",
  backgroundColor: BG,
  border: `1px solid ${BORDER_STRONG}`,
  borderRadius: "8px",
  padding: "7px 10px",
  color: "#fff",
  fontFamily: FONT_FAMILY,
  fontSize: "0.82rem",
  outline: "none",
  boxSizing: "border-box",
  colorScheme: "dark",
  "&:focus": { borderColor: "rgba(253,185,64,0.5)" },
};

export default function DateRangePill({ scope, from, to, onApply }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);

  const open = Boolean(anchorEl);
  const activePreset = RANGE_PRESETS.find((p) => {
    const [pFrom, pTo] = p.range();
    return pFrom === from && pTo === to;
  });
  const draftPreset = RANGE_PRESETS.find((p) => {
    const [pFrom, pTo] = p.range();
    return pFrom === draftFrom && pTo === draftTo;
  });

  const handleOpen = (e) => {
    // Re-sync the draft from the applied value each time, so a previous Cancel
    // can't leak stale dates into the next open.
    setDraftFrom(from);
    setDraftTo(to);
    setAnchorEl(e.currentTarget);
  };

  const handleApply = () => {
    if (draftFrom && draftTo && draftFrom > draftTo) return;
    onApply(draftFrom, draftTo);
    setAnchorEl(null);
  };

  const invalid = Boolean(draftFrom && draftTo && draftFrom > draftTo);

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={handleOpen}
        aria-expanded={open}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          padding: "6px 12px",
          backgroundColor: open ? "rgba(253,185,64,0.12)" : "rgba(255,255,255,0.03)",
          border: `1px solid ${open ? "rgba(253,185,64,0.45)" : BORDER_STRONG}`,
          borderRadius: "999px",
          color: open ? AMBER : "rgba(255,255,255,0.65)",
          fontFamily: FONT_FAMILY,
          fontSize: "0.78rem",
          fontWeight: 500,
          cursor: "pointer",
          textTransform: "none",
          letterSpacing: "normal",
          transition: "all 0.18s cubic-bezier(0.4,0,0.2,1)",
          "&:hover": { color: AMBER, borderColor: "rgba(253,185,64,0.35)", backgroundColor: "rgba(253,185,64,0.12)" },
        }}
      >
        <CalendarTodayOutlinedIcon sx={{ fontSize: "13px" }} />
        <Box component="span" sx={{ fontWeight: 600, color: "#fff", letterSpacing: "0.01em" }}>{scope}</Box>
        <Box component="span" sx={{ color: "rgba(255,255,255,0.35)" }}>·</Box>
        <Box component="span" sx={{ color: "#fff", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
          {activePreset ? activePreset.label : formatRange(from, to)}
        </Box>
        <KeyboardArrowDownIcon sx={{ fontSize: "11px", opacity: open ? 0.9 : 0.5, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.18s" }} />
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: "6px",
              minWidth: "320px",
              maxWidth: "360px",
              p: "14px 14px 12px",
              backgroundColor: CARD_BG,
              border: `1px solid ${BORDER_STRONG}`,
              borderRadius: "12px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.45)",
              backgroundImage: "none",
            },
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: "12px" }}>
          <Typography sx={{ fontSize: "0.68rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color: "rgba(255,255,255,0.35)" }}>
            Range
          </Typography>
          <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#fff" }}>{scope}</Typography>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", mb: "12px" }}>
          <Box>
            <Typography component="label" sx={{ display: "block", fontSize: "0.7rem", color: "rgba(255,255,255,0.5)", mb: "4px" }}>Start</Typography>
            <Box component="input" type="date" value={draftFrom} onChange={(e) => setDraftFrom(e.target.value)} sx={FIELD_INPUT_SX} />
          </Box>
          <Box>
            <Typography component="label" sx={{ display: "block", fontSize: "0.7rem", color: "rgba(255,255,255,0.5)", mb: "4px" }}>End</Typography>
            <Box component="input" type="date" value={draftTo} onChange={(e) => setDraftTo(e.target.value)} sx={FIELD_INPUT_SX} />
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mb: "10px" }}>
          {RANGE_PRESETS.map((preset) => {
            const isActive = draftPreset?.key === preset.key;
            return (
              <Box
                key={preset.key}
                component="button"
                type="button"
                onClick={() => {
                  const [pFrom, pTo] = preset.range();
                  setDraftFrom(pFrom);
                  setDraftTo(pTo);
                }}
                sx={{
                  padding: "5px 10px",
                  borderRadius: "999px",
                  fontFamily: FONT_FAMILY,
                  fontSize: "0.74rem",
                  cursor: "pointer",
                  // Solid amber fill when active — the shared popover's own treatment,
                  // deliberately different from the tinted chips on History/Export.
                  backgroundColor: isActive ? AMBER : "transparent",
                  border: `1px solid ${isActive ? AMBER : BORDER_STRONG}`,
                  color: isActive ? BG : "rgba(255,255,255,0.65)",
                  fontWeight: isActive ? 600 : 500,
                }}
              >
                {preset.label}
              </Box>
            );
          })}
        </Box>

        <Typography sx={{ fontSize: "0.7rem", color: invalid ? "#F87171" : "rgba(255,255,255,0.35)", mb: "12px" }}>
          {invalid ? "End date is before start date" : "Pick a preset or set an exact start and end date."}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "flex-end" }}>
          <FAGhostButton onClick={() => setAnchorEl(null)} sx={{ px: "14px", py: "8px", borderRadius: "8px", fontSize: "0.82rem" }}>
            Cancel
          </FAGhostButton>
          <FAPrimaryButton onClick={handleApply} disabled={invalid} sx={{ px: "14px", py: "8px", borderRadius: "8px", fontSize: "0.82rem" }}>
            Apply
          </FAPrimaryButton>
        </Box>
      </Popover>
    </>
  );
}
